import { z } from 'zod'
import { oc } from '@orpc/contract'

export const stediEnvironmentSchema = z.enum(['test', 'production'])

/**
 * Writes an organization's Stedi API key. **Every organization needs its own** — same
 * rule as Bridge: there is no environment-wide default, so this write is what turns Stedi
 * on for an organization.
 *
 * The key is write-only at this boundary: it is accepted on save, stored in
 * dynamic-settings, and never returned by any endpoint. There is no non-secret half to
 * echo back, so the status endpoint reports the *mode* instead — derived from the key's
 * own `test_` prefix, revealing nothing while still catching the mistake that matters,
 * a test key pasted into a production organization.
 *
 * The key **is** validated live before being persisted (one page of the payer directory,
 * a cheap read with no side effects), following the Bridge writer: a rejected key here is
 * silent until a claim fails, which is far too late to learn about it.
 */
export const setOrganizationStediKey = oc
  .route({ method: 'POST', path: '/v2/super/organizations/{organizationId}/stedi-key' })
  .input(
    z.object({
      organizationId: z.string().min(1),
      // Stedi documents no shape beyond the `test_` prefix on test keys, and pinning an
      // exact format here would reject a key Stedi itself accepts. The live check in the
      // handler is the real assertion.
      apiKey: z.string().trim().min(1),
    }),
  )
  .output(
    z.object({
      configured: z.literal(true),
      environment: stediEnvironmentSchema,
    }),
  )

export const getOrganizationStediStatus = oc
  .route({ method: 'GET', path: '/v2/super/organizations/{organizationId}/stedi-key' })
  .input(z.object({ organizationId: z.string().min(1) }))
  .output(
    z.object({
      configured: z.boolean(),
      environment: stediEnvironmentSchema.optional(),
    }),
  )
